/* ==========================================================================
   HYBEC AIR — 3D hero fan
   Ported from the approved hybec-air.html reference. Renders the animated
   ceiling fan (Three.js) inside the hero section container.
   - Loads Three.js lazily, only when the hero is on the page
   - Caps device pixel ratio, pauses when the tab is hidden or hero offscreen
   - Renders a single static frame under prefers-reduced-motion
   - Fails silently (hero content still fully usable without WebGL)
   ========================================================================== */
(function () {
  'use strict';

  var THREE_FALLBACK_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
  var GLTF_LOADER_SRC = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js';

  function loadThree(src) {
    return new Promise(function (resolve, reject) {
      if (window.THREE) return resolve(window.THREE);
      var s = document.createElement('script');
      s.src = src || THREE_FALLBACK_SRC;
      s.async = true;
      s.onload = function () {
        resolve(window.THREE);
      };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function loadGLTFLoader(src) {
    return new Promise(function (resolve, reject) {
      if (window.THREE && window.THREE.GLTFLoader) return resolve(window.THREE.GLTFLoader);
      var s = document.createElement('script');
      s.src = src || GLTF_LOADER_SRC;
      s.async = true;
      s.onload = function () {
        resolve(window.THREE.GLTFLoader);
      };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function initScene(container) {
    var THREE = window.THREE;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /* Stacked (centred) composition: phones, plus portrait-ish tablets where
       the desktop right-offset fan would slide under the hero copy. Must match
       the tablet hero rules in hybec-tablet.css. */
    var stackedMQ = window.matchMedia(
      '(max-width: 749px), (min-width: 750px) and (max-width: 1023px), (min-width: 1024px) and (max-width: 1199px) and (orientation: portrait)'
    );
    var isMobile = stackedMQ.matches;

    var canvas = document.createElement('canvas');
    container.appendChild(canvas);

    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
    } catch (e) {
      container.remove();
      return;
    }

    var width = container.clientWidth || window.innerWidth;
    var height = container.clientHeight || window.innerHeight;

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    /* No cinematic tone mapping/grading — the fan should render in its
       actual material colors, not a stylized color grade. */
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.outputEncoding = THREE.sRGBEncoding;

    var scene = new THREE.Scene();
    /* Lighter, less dense fog — enough to blend the model into the
       background at its edges without hazing over the fan itself. */
    scene.fog = new THREE.FogExp2(0x1a1a1c, 0.022);

    /* Camera sits BELOW the fan's centerline and looks slightly upward —
       a ceiling fan is mounted overhead, so the natural/expected view is
       from underneath (showing the blade undersides + motor housing
       base), not a level or downward-looking angle. */
    var camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 100);
    /* Level camera (no pitch or yaw), so vertical lines stay vertical and
       the downrod/canopy read perfectly straight. The framing that used to
       come from tilting the camera upward now comes from shifting the
       projection (lens shift), so the view from underneath is unchanged. */
    var cameraBaseY = -1.3;
    var LENS_SHIFT = 0.282; // fraction of frame height to shift the view upward
    function applyLensShift(w, h) {
      camera.setViewOffset(w, h, 0, -h * LENS_SHIFT, w, h);
    }
    camera.position.set(0, cameraBaseY, 7.2);
    camera.lookAt(0, cameraBaseY, 0);
    applyLensShift(width, height);

    /* ---------- Lighting ----------
       A neutral white key light does the actual work of showing the fan's
       real material colors; the brand-green lights are kept as a subtle
       rim/backlight accent only, not the dominant light source — the
       previous balance (strong green rim + green backlight against one
       dim white light) tinted the whole model green like a color filter. */
    scene.add(new THREE.AmbientLight(0x606468, 0.9));

    var key = new THREE.DirectionalLight(0xffffff, 1.3);
    key.position.set(-3, 3, 6);
    scene.add(key);

    var fillWhite = new THREE.DirectionalLight(0xffffff, 0.4);
    fillWhite.position.set(4, -1, 4);
    scene.add(fillWhite);

    var rim = new THREE.DirectionalLight(0xa6ce39, 0.6);
    rim.position.set(5, 4, -3);
    scene.add(rim);

    var fill = new THREE.PointLight(0x6b8533, 0.2, 15);
    fill.position.set(0, -3, 2);
    scene.add(fill);

    var back = new THREE.PointLight(0xa6ce39, 0.5, 10);
    back.position.set(0, 0, -3);
    scene.add(back);

    /* ---------- Fan model ----------
       Loads the actual Hybec fan (HYFC-360, healed + tessellated from the
       manufacturer's CAD file) as a two-part glTF: 'hy_fan_blades' spins,
       'hy_fan_body' (motor, downrod, canopy, hardware) stays fixed. */
    var fanGroup = new THREE.Group();
    scene.add(fanGroup);

    var rotorGroup = new THREE.Group(); // spins
    var stillGroup = new THREE.Group(); // mount + housing
    fanGroup.add(rotorGroup);
    fanGroup.add(stillGroup);

    var modelUrl = container.dataset.hyFanModel;
    if (modelUrl && THREE.GLTFLoader) {
      var gltfLoader = new THREE.GLTFLoader();
      gltfLoader.load(
        modelUrl,
        function (gltf) {
          /* Force double-sided materials — the source mesh may only have
             normals facing "up" (as authored for a top-down view), which
             would make the blades/housing invisible/backface-culled once
             the camera is looking up at them from below. */
          gltf.scene.traverse(function (node) {
            if (node.isMesh && node.material) {
              var mats = Array.isArray(node.material) ? node.material : [node.material];
              mats.forEach(function (m) {
                m.side = THREE.DoubleSide;
              });
            }
          });

          var blades = gltf.scene.getObjectByName('hy_fan_blades');
          var body = gltf.scene.getObjectByName('hy_fan_body');
          if (blades) rotorGroup.add(blades);
          if (body) stillGroup.add(body);
          if (!blades && !body) {
            /* Names didn't survive export for some reason — show the whole
               model as static rather than nothing. */
            stillGroup.add(gltf.scene);
          }
        },
        undefined,
        function () {
          /* Model failed to load — rings/particles/lighting still render. */
        }
      );
    }

    /* Off-center on desktop for the editorial composition; centered on
       mobile. Raised toward the top of the scene (was y=-0.2, which sat
       roughly mid-frame) so the fan reads as mounted near the header
       instead of floating in the middle of empty space. */
    var fanBaseY = isMobile ? -0.1 : 0.55;
    fanGroup.position.set(isMobile ? 0 : 4.6, fanBaseY, 0);
    /* Re-centre / re-offset if a tablet is rotated after load. */
    var onStackedChange = function (e) {
      fanGroup.position.set(e.matches ? 0 : 4.6, e.matches ? -0.1 : 0.55, 0);
    };
    if (stackedMQ.addEventListener) stackedMQ.addEventListener('change', onStackedChange);
    else if (stackedMQ.addListener) stackedMQ.addListener(onStackedChange);
    /* Bigger, more presentable presence in the hero — uniform scale keeps
       the model's real proportions intact rather than stretching it. */
    var fanScale = isMobile ? 1.3 : 1.3;
    fanGroup.scale.set(fanScale, fanScale, fanScale);

    /* ---------- Decorative concentric rings ---------- */
    var decoRings = new THREE.Group();
    for (var r = 0; r < 3; r++) {
      var radius = 3.2 + r * 0.4;
      var ring = new THREE.Mesh(
        new THREE.RingGeometry(radius, radius + 0.004, 128),
        new THREE.MeshBasicMaterial({
          color: 0xa6ce39,
          transparent: true,
          opacity: 0.06 - r * 0.015,
          side: THREE.DoubleSide,
        })
      );
      ring.rotation.x = Math.PI / 2;
      ring.userData.speed = 0.0008 + r * 0.0004;
      decoRings.add(ring);
    }
    decoRings.position.copy(fanGroup.position);
    decoRings.position.y -= 0.2;
    scene.add(decoRings);

    /* ---------- Particles (floating air/dust) ---------- */
    var particleCount = isMobile ? 250 : 600;
    var particleGeo = new THREE.BufferGeometry();
    var positions = new Float32Array(particleCount * 3);
    var velocities = new Float32Array(particleCount * 3);
    var sizes = new Float32Array(particleCount);

    for (var p = 0; p < particleCount; p++) {
      positions[p * 3] = (Math.random() - 0.5) * 18;
      positions[p * 3 + 1] = (Math.random() - 0.5) * 10;
      positions[p * 3 + 2] = (Math.random() - 0.5) * 12 - 2;
      velocities[p * 3] = (Math.random() - 0.5) * 0.001;
      velocities[p * 3 + 1] = (Math.random() - 0.5) * 0.0015 + 0.0005;
      velocities[p * 3 + 2] = (Math.random() - 0.5) * 0.001;
      sizes[p] = Math.random() * 0.04 + 0.005;
    }
    particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particleGeo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    var particleMat = new THREE.ShaderMaterial({
      uniforms: {
        color: { value: new THREE.Color(0xd8f2a0) },
        time: { value: 0 },
      },
      vertexShader: [
        'attribute float size;',
        'varying float vAlpha;',
        'uniform float time;',
        'void main() {',
        '  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);',
        '  gl_PointSize = size * (300.0 / -mvPosition.z);',
        '  gl_Position = projectionMatrix * mvPosition;',
        '  vAlpha = 0.4 + 0.4 * sin(time * 0.5 + position.x * 2.0);',
        '}',
      ].join('\n'),
      fragmentShader: [
        'uniform vec3 color;',
        'varying float vAlpha;',
        'void main() {',
        '  vec2 c = gl_PointCoord - vec2(0.5);',
        '  float d = length(c);',
        '  if (d > 0.5) discard;',
        '  float alpha = (1.0 - d * 2.0) * vAlpha;',
        '  gl_FragColor = vec4(color, alpha * 0.7);',
        '}',
      ].join('\n'),
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    var particles = new THREE.Points(particleGeo, particleMat);
    scene.add(particles);

    /* ---------- Spin state (CTA click nudges the fan to a gentle, slowed
       running speed — not full speed; the fan itself never moves/tilts,
       boosted or not, only the blade spin speed changes) ---------- */
    var spinSpeed = 0.012;
    var baseSpin = 0.012;
    var boostSpin = 0.06;
    var isBoosted = false;

    var boostTimer = null;
    document.querySelectorAll('[data-hy-fan-boost]').forEach(function (el) {
      el.addEventListener('click', function () {
        isBoosted = true;
        el.setAttribute('aria-pressed', 'true');
        // Slowed running speed for 10s, then ease back to idle.
        if (boostTimer) window.clearTimeout(boostTimer);
        boostTimer = window.setTimeout(function () {
          isBoosted = false;
          el.setAttribute('aria-pressed', 'false');
        }, 10000);
      });
    });

    /* ---------- Render loop ---------- */
    var clock = new THREE.Clock();
    var running = true;

    function renderFrame() {
      var t = clock.getElapsedTime();

      var targetSpin = isBoosted ? boostSpin : baseSpin;
      spinSpeed += (targetSpin - spinSpeed) * (isBoosted ? 0.06 : 0.025);

      /* Spin in the direction the blades are pitched for (forward/downdraft):
         the higher edge of each blade leads. For this model that is a
         negative rotation about Y. */
      rotorGroup.rotation.y -= spinSpeed;

      /* The fan itself stays straight/upright at all times — only the
         blades spin (above) and the camera parallaxes around it (below).
         It used to also tilt with the cursor, which made the whole body,
         including the motor housing up top, sway off-vertical. */
      fanGroup.position.y = fanBaseY + Math.sin(t * 0.6) * 0.04;

      decoRings.children.forEach(function (ringMesh) {
        ringMesh.rotation.z += ringMesh.userData.speed;
      });

      var posArr = particleGeo.attributes.position.array;
      for (var i = 0; i < particleCount; i++) {
        posArr[i * 3] += velocities[i * 3];
        posArr[i * 3 + 1] += velocities[i * 3 + 1];
        posArr[i * 3 + 2] += velocities[i * 3 + 2];
        posArr[i * 3] += (fanGroup.position.x - posArr[i * 3]) * 0.0001;
        if (posArr[i * 3 + 1] > 5) posArr[i * 3 + 1] = -5;
        if (posArr[i * 3 + 1] < -5) posArr[i * 3 + 1] = 5;
        if (posArr[i * 3] > 9) posArr[i * 3] = -9;
        if (posArr[i * 3] < -9) posArr[i * 3] = 9;
      }
      particleGeo.attributes.position.needsUpdate = true;
      particleMat.uniforms.time.value = t;

      /* Camera stays fixed — the fan should hold perfectly still, not
         shift/parallax as the cursor moves. */
      camera.position.x = fanGroup.position.x * 0.5;
      camera.lookAt(camera.position.x, cameraBaseY, 0);

      back.intensity = 0.5 + Math.sin(t * 1.2) * 0.15;

      renderer.render(scene, camera);
    }

    if (reducedMotion) {
      renderFrame();
      return;
    }

    function animate() {
      if (!running) return;
      requestAnimationFrame(animate);
      renderFrame();
    }
    animate();

    /* Pause when tab hidden or hero scrolled away */
    document.addEventListener('visibilitychange', function () {
      var shouldRun = !document.hidden;
      if (shouldRun && !running) {
        running = true;
        animate();
      } else if (!shouldRun) {
        running = false;
      }
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          var visible = entries[0].isIntersecting;
          if (visible && !running) {
            running = true;
            animate();
          } else if (!visible) {
            running = false;
          }
        },
        { threshold: 0 }
      ).observe(container);
    }

    /* ---------- Resize ---------- */
    function onResize() {
      var w = container.clientWidth || window.innerWidth;
      var h = container.clientHeight || window.innerHeight;
      camera.aspect = w / h;
      applyLensShift(w, h);
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      if (reducedMotion) renderFrame();
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(onResize).observe(container);
    } else {
      window.addEventListener('resize', onResize);
    }
  }

  function boot() {
    var container = document.querySelector('[data-hy-fan-scene]');
    if (!container || container.dataset.hyFanInit) return;
    container.dataset.hyFanInit = '1';

    loadThree(container.dataset.threeSrc)
      .then(function () {
        return loadGLTFLoader(container.dataset.gltfLoaderSrc).catch(function () {
          /* GLTFLoader failed — scene still renders (rings/particles/lighting),
             just without the fan mesh loaded in. */
        });
      })
      .then(function () {
        initScene(container);
      })
      .catch(function () {
        /* three.js unavailable — hero remains as styled static content */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
